import { useMemo } from "react";
import type { SWRConfiguration, SWRResponse } from "swr";

import { type ResponseStatus, type IReturn } from "src/api/Tmd.dtos";
import useApi from "src/hooks/api/useApi";

/**
 * Defines the response payload and status expected by this shared request adapter.
 */
export type TMDLoadCollectionResponse<TModel> = {
    responseStatus: ResponseStatus;
    models: TModel[];
};

/**
 * Defines the typed request payload constructed by this shared adapter.
 */
export type TMDLoadCollectionRequest<
    TModel,
    TResponse extends TMDLoadCollectionResponse<TModel>
> = IReturn<TResponse> & {};

/**
 * Describes projection of a response into its model collection.
 */
export type UseApiGetDataFn<TModel, TResponse extends TMDLoadCollectionResponse<TModel>> = (
    response?: SWRResponse<TResponse, TResponse>
) => TModel[];

const getData = <TModel, TResponse extends TMDLoadCollectionResponse<TModel>>(
    response?: SWRResponse<TResponse, TResponse>
) => response?.data?.models;
const getError = <TModel, TResponse extends TMDLoadCollectionResponse<TModel>>(
    response?: SWRResponse<TResponse, TResponse>
) => (response?.error ?? response?.data)?.responseStatus;
const getAdditionalState = <TModel, TResponse extends TMDLoadCollectionResponse<TModel>>(
    _response?: SWRResponse<TResponse, TResponse>
) => ({});

/**
 * Loads a model collection from a GET request recreated from its serialized options.
 *
 * Preconditions: Request options must be JSON-serializable, even when loading is disabled.
 *
 * Postconditions: Missing constructor or disabled state suppresses the request. JSON
 * serialization determines memo identity and reconstructs the request payload; model arrays are
 * projected unchanged.
 */
const useBaseLoadCollection = <
    TModel,
    TResponse extends TMDLoadCollectionResponse<TModel>,
    TRequest extends TMDLoadCollectionRequest<TModel, TResponse>
>(
    constructor: new (init?: Partial<TRequest>) => TRequest,
    configuration?: SWRConfiguration<TResponse, TResponse> | null | undefined,
    request?: Partial<TRequest> | null | undefined,
    enabled = true
) => {
    const requestKey = JSON.stringify(request);
    const getRequest = useMemo(
        () => {
            if (!constructor || !enabled) {
                return undefined;
            }

            return (): IReturn<TResponse> =>
                new constructor(
                    requestKey
                        ? (JSON.parse(requestKey) as Partial<TRequest>)
                        : ({} as Partial<TRequest>)
                );
        },
        [constructor, enabled, requestKey]
    );

    return useApi(
        "get",
        getRequest,
        { getData: getData as UseApiGetDataFn<TModel, TResponse>, getError, getAdditionalState },
        configuration
    );
};

export default useBaseLoadCollection;
